import { ID, PaginationResult } from "@/types/common"
import { createCrudApi } from "@/utils/apiFactory"
import http from "@/utils/request"
import { Lead, LeadFormData } from "../types"
import { LEAD_DICT_CODES } from "../constants"

const baseApi = createCrudApi<Lead, LeadFormData, LeadFormData>("/leads")

type DictTreeNode = {
  id: ID
  parentId: ID | null
  nodeType: "CATEGORY" | "ITEM"
  name: string
  code: string
  isEnabled: boolean
  children?: DictTreeNode[]
}

function collectDictOptions(
  tree: DictTreeNode[],
  categoryCode: string
): Array<{ label: string; value: string }> {
  const category = tree.find(
    (item) => item.nodeType === "CATEGORY" && item.code === categoryCode
  )
  if (!category?.children?.length) {
    return []
  }

  const options: Array<{ label: string; value: string }> = []
  const walk = (items: DictTreeNode[]) => {
    items.forEach((item) => {
      if (item.nodeType === "ITEM" && item.isEnabled) {
        options.push({
          label: item.name,
          value: item.code,
        })
      }
      if (item.children?.length) {
        walk(item.children)
      }
    })
  }

  walk(category.children)
  return options
}

const leadApi = {
  async getList(params?: {
    page?: number
    pageSize?: number
    month?: string
  }): Promise<PaginationResult<Lead>> {
    return baseApi.list(params)
  },

  async getById(id: ID): Promise<Lead | null> {
    return baseApi.getById(id)
  },

  async create(data: LeadFormData): Promise<void> {
    await baseApi.create({
      studentName: data.studentName,
      leadAt: data.leadAt,
      sourceType: data.sourceType,
      retentionType: data.retentionType,
      wechatAdded: data.wechatAdded,
      storeEvaluated: data.storeEvaluated,
      evaluationNote: data.evaluationNote,
      trialLessonNote: data.trialLessonNote,
      note: data.note,
    })
  },

  async update(id: ID, data: LeadFormData): Promise<void> {
    await baseApi.update(id, {
      studentName: data.studentName,
      leadAt: data.leadAt,
      sourceType: data.sourceType,
      retentionType: data.retentionType,
      wechatAdded: data.wechatAdded,
      storeEvaluated: data.storeEvaluated,
      evaluationNote: data.evaluationNote,
      trialLessonNote: data.trialLessonNote,
      note: data.note,
    })
  },

  async delete(ids: ID[]): Promise<void> {
    await baseApi.delete(ids)
  },

  async getDictOptions(): Promise<{
    sourceOptions: Array<{ label: string; value: string }>
    retentionTypeOptions: Array<{ label: string; value: string }>
  }> {
    const res = await http.get<PaginationResult<DictTreeNode>>("/system-dicts")
    const tree = res.data?.data ?? []
    return {
      sourceOptions: collectDictOptions(tree, LEAD_DICT_CODES.SOURCE),
      retentionTypeOptions: collectDictOptions(tree, LEAD_DICT_CODES.RETENTION_TYPE),
    }
  },
}

export default leadApi
